import { AxiosError, HttpStatusCode } from 'axios';
import { Box, Typography } from '@mui/material';
import { FC, Fragment, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCheckBookingStatus } from 'src/lib/hooks/use-get-check-booking-status';
import { Spinner } from 'src/components/spinner';
import { routes } from 'src/constants/routes';
import { BookingFlow, BookingStatus, ErrorReason } from 'src/shared/enum/BookingStatus';
import { removeStorage } from 'src/utils/local-storage-versioning';
import { getTheme } from 'src/utils/getTheme';
import { useTrackPurchaseEvent } from 'src/lib/hooks/use-track-purchase-event';

import { styles } from './styles';

const { isHosTheme } = getTheme();

const TextWrapper: FC<{ children: ReactNode }> = (props) => {
    const Wrapper = isHosTheme ? Box : Fragment;

    return <Wrapper>{props.children}</Wrapper>;
};

export const ProcessingPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { query } = useCheckBookingStatus();
    const trackPurchaseEvent = useTrackPurchaseEvent();

    const { error, data } = query;

    useEffect(() => {
        if (error) {
            if (
                error instanceof AxiosError &&
                error.response &&
                error.response.status === HttpStatusCode.NotFound
            ) {
                return;
            }
            navigate(routes.failure.index);
            return;
        }

        if (!data) return;
        if (data.booking_status === BookingStatus.ERROR) {
            let errorReason;

            if (data.booking_flow === BookingFlow.NON_PAYMENT_BOOKING) {
                errorReason = ErrorReason.BOOKING_WITHOUT_PAYMENT_FAILED;
            } else if (data.booking_flow === BookingFlow.STANDARD_BOOKING) {
                errorReason = ErrorReason.BOOKING_FAILED;
            }

            if (errorReason) {
                navigate(`${routes.failure.index}?reason=${encodeURIComponent(errorReason)}`);
            }
        } else if (data.booking_status === BookingStatus.SUCCESS) {
            trackPurchaseEvent();
            removeStorage();
            navigate(routes.success.index);
        }
    }, [error, data, navigate, routes.failure.index, routes.success.index]);

    return (
        <Box css={styles.container}>
            <Box css={styles.imageContainer} />

            <Box css={styles.wrapper}>
                <TextWrapper>
                    <Typography variant="h1" css={styles.title}>
                        {t('booking_processing')}
                    </Typography>

                    <Typography variant="body3">{t('processing_info')}</Typography>
                </TextWrapper>

                <Spinner />
            </Box>
        </Box>
    );
};
